import React, { useState } from "react";
import { observer } from "mobx-react";
import { makeApi, URLS } from "Utils/apiURL";
import SellerProfileFormBody, {
  buildEmptyProfile,
  hydrateProfileForEdit
} from "./SellerProfileFormBody";
import SellerIntelligencePanel from "./SellerIntelligencePanel";

const SellerProfileSetup = observer(({ onSave, onClose, existingProfile }) => {
  const [step, setStep] = useState(existingProfile ? "edit" : "url");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(() =>
    existingProfile ? hydrateProfileForEdit(existingProfile) : buildEmptyProfile()
  );

  const enrichFromWebsite = () => {
    if (!website.trim()) return;
    setLoading(true);
    setError("");
    makeApi({
      url: URLS.deepResearchEnrichSeller,
      method: "POST",
      data: { website: website.trim() }
    })
      .then((res) => {
        if (res?.data && !res.data.error) {
          setProfile({ ...profile, ...res.data, website: website.trim() });
          setStep("edit");
        } else {
          setError("Could not fetch data. Please fill manually.");
          setStep("edit");
          setProfile({ ...profile, website: website.trim() });
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to connect. Please fill manually.");
        setStep("edit");
        setProfile({ ...profile, website: website.trim() });
        setLoading(false);
      });
  };

  const handleSave = () => {
    if (onSave) onSave({ ...profile, isActive: true });
  };

  // ── URL Input Step ──
  if (step === "url") {
    return (
      <div className="drPopupOverlay" onClick={onClose}>
        <div className="drPopup" onClick={(e) => e.stopPropagation()}>
          <div className="drPopupHeader">
            <h3>Set Up Seller Profile</h3>
            <span className="drPopupClose" onClick={onClose}>
              ✕
            </span>
          </div>
          <div className="drPopupBody">
            <p className="drPopupIntro">
              Enter your company website and we'll auto-fill your seller profile.
            </p>
            <div className="drInputGroup">
              <label>Company Website</label>
              <input
                type="text"
                placeholder="e.g. salesgear.io"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && enrichFromWebsite()}
                autoFocus
              />
            </div>
            {error && <p className="drPopupError">{error}</p>}
          </div>
          <div className="drPopupFooter">
            <button
              className="drPopupBtn secondary"
              onClick={() => setStep("edit")}
            >
              Fill Manually
            </button>
            <button
              className="drPopupBtn primary"
              onClick={enrichFromWebsite}
              disabled={loading || !website.trim()}
            >
              {loading ? "Analyzing..." : "Auto-Fill →"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Edit Step ──
  // For existing profiles: 2-col layout (form left, AI intelligence right).
  // For new creates: single column (panel can't render until first save).
  const showIntel = !!existingProfile?.id;
  return (
    <div className="drPopupOverlay" onClick={onClose}>
      <div
        className={`drPopup ${showIntel ? "drPopupSplit" : "drPopupLarge"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="drPopupHeader">
          <h3>{existingProfile ? "Edit Seller Profile" : "Seller Profile"}</h3>
          <span className="drPopupClose" onClick={onClose}>
            ✕
          </span>
        </div>
        <div className={`drPopupBody drPopupScroll ${showIntel ? "drPopupBody--split" : ""}`}>
          <div className="drPopupBody__form">
            <SellerProfileFormBody profile={profile} setProfile={setProfile} />
          </div>
          {showIntel && (
            <div className="drPopupBody__intel">
              <SellerIntelligencePanel
                sellerProfileId={existingProfile.id}
                sellerProfileName={existingProfile.name || existingProfile.companyName}
              />
            </div>
          )}
        </div>
        <div className="drPopupFooter">
          <button className="drPopupBtn secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="drPopupBtn primary" onClick={handleSave}>
            Save Seller Profile
          </button>
        </div>
      </div>
    </div>
  );
});

export default SellerProfileSetup;
